import React from 'react';
import { useSelector } from 'react-redux';
import { Card, Carousel, Icon, Typography } from '@drone-js/core';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { TopActivityDestinationItem } from 'component/page/home/TopActivitiesDestinationContainer/TopActivityDestinationItem/TopActivityDestinationItem';
import { useLazyGetTopDestinationResultQuery } from 'store/api/activitiesApi';
import { selectPageTypeId } from 'store/slice/navigation';
import { StringFormatter } from '@agoda/react-handyman';
import { TopDestinationsResponse } from 'store/api/api.types';
import 'component/page/home/TopActivitiesDestinationContainer/TopActivitiesDestinationContainer.scss';
import { getTopDestinations } from 'component/common/TopDestinations/topDestinationUtils';
import { TopDestination } from 'core/type/ActivitiesTopDestinations.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesFieldsV1,
    ActivitiesTopDestinationContainerSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesManifestV1,
    ActivitiesTopDestinationContainerSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';

export const TopActivitiesDestinationContainer: React.FC = () => {
    const [triggerGetTopDestinationResult, topDestinationResponse] = useLazyGetTopDestinationResultQuery();
    const config = useSelector(selectConfigFromBootstrap);
    const cms = config.cms;
    const pageTypeId = useSelector(selectPageTypeId);

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        triggerGetTopDestinationResult({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const topDestinationData = React.useMemo(
        () => getTopDestinations(topDestinationResponse as unknown as TopDestinationsResponse, pageTypeId, config),
        [topDestinationResponse, pageTypeId, config],
    );

    const topDomesticDestinations = topDestinationData.topDomesticDestinations;
    React.useEffect(() => {
        topDomesticDestinations.length > 0 &&
            trackElementEvent<ActivitiesTopDestinationContainerSeenEventActivitiesFieldsV1>(ActivitiesTopDestinationContainerSeenEventActivitiesManifestV1.ids, {});
    }, [topDomesticDestinations]);

    if (!topDestinationData || !topDomesticDestinations || topDomesticDestinations.length === 0) {
        return null;
    }

    const domesticCountry = topDestinationData.domesticCountry;
    const topDestinationsCms = StringFormatter.format(cms[153679], domesticCountry?.countryName || '');
    const heading = domesticCountry?.languageId === config.language.languageId ? topDestinationsCms : (domesticCountry?.topDomesticCityFallback ?? topDestinationsCms);
    const onScroll = (index: number, length: number, scrollLeft: number, scrollDirection: 'next' | 'previous') => {
        trackElementEvent<ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesFieldsV1>(
            ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesManifestV1.ids,
            {
                action_element_value: scrollDirection,
            },
        );
    };

    return (
        <Card
            data-testid="activities-top-destination-container"
            data-element-name="activities-top-destination-container"
            mt="32"
            p="12"
            data-fewer-items={topDomesticDestinations.length <= 3}
        >
            <div>
                <Icon id="fill.activity.ferris-wheel" size="14" />
                <Typography tag="span" variant="body-s-strong" ps="4">
                    {cms[153678]}
                </Typography>
            </div>
            <Typography variant="body-default" mt="8" mb="12">
                {heading}
            </Typography>
            <Carousel
                data-testid="activities-top-destination-carousel"
                currentCard={0}
                loading="eager"
                fade="visible"
                cardWidth={156}
                accessibilityNextNavLabel={cms[130706]}
                accessibilityPreviousNavLabel={cms[130765]}
                onScroll={onScroll}
                mb="-16"
            >
                {topDomesticDestinations.map((destination: TopDestination, index) => (
                    <TopActivityDestinationItem destination={destination} key={destination.cityId} index={index} />
                ))}
            </Carousel>
        </Card>
    );
};
TopActivitiesDestinationContainer.displayName = 'TopActivitiesDestinationContainer';
